import { useMemo, useState } from "react";
import type { AvailableMatkul, CategoryData, RecommendationResult } from "@/types";
import categoryJson from "@/data/category.json";
import { MatkulCard } from "./MatkulCard";

interface RekomResultProps {
    recoms: RecommendationResult;
    /**
     * Mode "selected" → hanya tampilkan matkul dari array ID ini (RekomendasiMsg)
     * Mode "all"      → tampilkan semua availableMatkuls, dimming yang tidak dipilih (AboutPage)
     */
    mode: "selected" | "all";
    selectedItemIds: number[];
    /** Header + footnote hanya tampil di mode "all" */
    showListHeader?: boolean;
}
const { categoryMap } = categoryJson as CategoryData;

export const RekomResult = ({
    recoms,
    mode,
    selectedItemIds,
    showListHeader = false,
}: RekomResultProps) => {
    const { matkuls: availableMatkuls, categories, categoryMatkuls, topCategoryKey } = recoms;
    const [activeTooltipId, setActiveTooltipId] = useState<number | null>(null);

    const [isSorted, setIsSorted] = useState(false);

    // Hitung jumlah matkul terpilih per kategori
    const countCatSelected = useMemo(() => {
        const countMap: Record<string, number> = {};
        for (const mkid of selectedItemIds) {
            const matkul = availableMatkuls[mkid];
            if (!matkul || matkul.catId == null) continue;
            const catId = String(matkul.catId);
            countMap[catId] = (countMap[catId] ?? 0) + 1;
        }
        return countMap;
    }, [selectedItemIds, availableMatkuls]);

    // Daftar matkul yang akan dirender
    const matkulList: AvailableMatkul[] = useMemo(() => {
        // 1. Ambil list awal berdasarkan mode
        let list = mode === "selected"
            ? selectedItemIds.map((id) => availableMatkuls[id]).filter(Boolean)
            : Object.values(availableMatkuls);

        // 2. Jika isSorted true, urutkan berdasarkan rank DESC
        if (isSorted) {
            // Gunakan [...list] agar tidak memutasi (mutate) array asli
            list = [...list].sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0));
        }

        return list;
    }, [mode, selectedItemIds, availableMatkuls, isSorted]);

    return (
        <div className="flex flex-col gap-3 animate-in fade-in slide-in-from-top-2 duration-300">

            {/* === SCOREBOARD KATEGORI === */}
            <div className="flex flex-col gap-1">
                <div className="px-1 opacity-50 font-black text-xxs uppercase tracking-tighter mb-1">
                    Kategori Relevan
                </div>
                {categories.map((cat, idx) => {
                    const isTop = idx === 0;
                    const maxScore = categories[0]?.totalScore ?? 1;
                    const barWidth = Math.round((cat.totalScore / maxScore) * 100);

                    return (
                        <div
                            key={cat.category}
                            className={`relative flex items-center gap-2 px-2 py-1.5 border-2 overflow-hidden
                                ${isTop
                                    ? 'border-black dark:border-neo-yellow bg-neo-yellow text-black shadow-[3px_3px_0_0_#000] dark:shadow-[3px_3px_0_0_#facc15]'
                                    : 'border-black dark:border-zinc-700 bg-white dark:bg-zinc-900'
                                }`}
                        >
                            {!isTop && (
                                <div
                                    className="absolute inset-y-0 left-0 bg-zinc-100 dark:bg-zinc-800"
                                    style={{ width: `${barWidth}%` }}
                                />
                            )}

                            <span className={`relative z-10 text-xxs font-black w-4 shrink-0 opacity-60 ${isTop ? 'text-black' : ''}`}>
                                #{idx + 1}
                            </span>

                            <span className={`relative z-10 flex-1 text-xxs font-black ${isTop ? 'text-black' : ''}`}>
                                {categoryMap[cat.category]}
                            </span>

                            <span className={`relative z-10 text-as font-bold opacity-60 shrink-0 hidden sm:inline ${isTop ? 'text-black' : ''}`}>
                                {cat.count} matkul
                            </span>

                            {/* null atau 0 akan ditolak */}
                            {countCatSelected[cat.category] && (
                                <span className={`relative z-10 text-as font-bold opacity-60 shrink-0 hidden sm:inline ${isTop ? 'text-black' : ''}`}>
                                    ({countCatSelected[cat.category]} dipilih)
                                </span>
                            )}

                            <div className={`relative z-10 flex flex-col items-end shrink-0 min-w-10.5 px-1.5 py-0.5
                                    ${isTop
                                    ? 'bg-black text-neo-yellow'
                                    : 'bg-neo-yellow text-black border-l border-black dark:border-neo-yellow'
                                }`}>
                                <span className="text-[6px] font-black uppercase leading-none">Score</span>
                                <span className="text-xxs font-black leading-tight">
                                    {cat.totalScore.toFixed(3)}
                                </span>
                                {/* Mobile only: tampil di bawah score */}
                                <span className="sm:hidden text-[6px] font-bold opacity-60 leading-none mt-0.5">
                                    {cat.count} mtkl
                                    {countCatSelected[cat.category] ? ` · ${countCatSelected[cat.category]}✓` : ''}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* === KURIKULUM KATEGORI TERATAS === */}
            {categoryMatkuls && categoryMatkuls.length > 0 && (
                <div className="flex flex-col gap-1">
                    <div className="px-1 opacity-50 font-black text-xxs uppercase tracking-tighter mb-1">
                        Kurikulum — {categoryMap[categories[0].category] ?? `Kategori ${topCategoryKey}`} (Matkul non riwayat)
                    </div>
                    <div className="flex flex-col gap-1">
                        {categoryMatkuls.map((cm, idx) => {
                            const smt = cm.jadwalSmtOrd ? cm.jadwalSmtOrd : (cm.jadwalSmtBin === 0 ? 'Genap' : 'Ganjil');
                            return (
                                <div
                                    key={cm.kode ?? idx}
                                    className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 px-2 py-1.5 border border-black dark:border-zinc-700 bg-white dark:bg-zinc-900"
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="text-as font-black opacity-40 shrink-0 w-6 text-right">
                                            Smt {smt}
                                        </span>
                                        <span className="text-as font-black border border-black dark:border-zinc-600 px-1 shrink-0 opacity-70">
                                            {cm.kode}
                                        </span>
                                        <span className="text-as opacity-50 shrink-0 sm:hidden">{cm.sks} SKS</span>
                                        {cm.wajib === 1 && (
                                            <span className="text-bs font-black px-1 bg-neo-yellow text-black border border-black shrink-0 sm:hidden">
                                                Wajib
                                            </span>
                                        )}
                                    </div>

                                    {/* Nama matkul dengan popover */}
                                    <span className="flex-1 text-xxs font-semibold block cursor-pointer">
                                        {cm.name}
                                    </span>

                                    {/* dekstop (di akhir) */}
                                    <span className="text-as opacity-50 shrink-0 hidden sm:inline">{cm.sks} SKS</span>
                                    {cm.wajib === 1 && (
                                        <span className="text-bs font-black px-1 bg-neo-yellow text-black border border-black shrink-0 hidden sm:inline">
                                            Wajib
                                        </span>
                                    )}
                                    {cm.prasyarat && (
                                        <div className="flex items-center gap-1">
                                            <span className="text-bs font-black opacity-40 shrink-0">Prasyarat</span>
                                            <span className="text-as opacity-70 truncate">{cm.prasyarat}</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* === HEADER DAFTAR (hanya mode all) === */}
            {showListHeader && (
                <>
                    <div className="flex justify-between items-end px-1 font-black text-xxs uppercase tracking-tighter text-neutral-800 dark:text-neutral-200">
                        <span>Matkul yang anda pilih</span>
                        <button onClick={() => setIsSorted(!isSorted)} className={`flex items-center gap-1 px-2 py-1 transition-all cursor-pointer font-bold active:translate-x-0 active:translate-y-0 active:shadow-none border-(--border-width-neo)
                            ${isSorted ? `bg-neo-blue text-white border-(--border-width-neo) shadow-(--shadow-neo) -translate-x-0.5 -translate-y-0.5` : 'bg-neo-white-warm text-black  shadow-(--shadow-neo-sm) hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-(--shadow-neo)'}`}>
                            <span>{isSorted ? 'Sorted' : 'Sort'}</span>
                            {isSorted ? <svg className="w-3 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 15l7-7 7 7" /></svg>
                                : <svg className="w-3 h-3.5 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>}
                        </button>

                        <span>
                            Rank / Score <span className="hidden md:inline">(kemungkinan lulus)</span>
                        </span>
                    </div>
                    <p className="text-xxs font-bold opacity-60 normal-case leading-tight">
                        *Dalam daftar hasil rekomendasi Neural Collaborative Filtering
                    </p>
                </>
            )}

            {/* === DAFTAR MATA KULIAH === */}
            <div className={
                mode === "all"
                    ? "max-h-[50vh] md:max-h-[80vh] overflow-y-auto px-1 py-2 flex flex-col gap-3 border-y border-zinc-200/50 dark:border-zinc-800/50"
                    : "flex flex-col gap-3"
            }>
                {matkulList.map((mk) => (
                    <MatkulCard
                        key={mk.itemId}
                        mk={mk}
                        isSelected={mode === "all" ? selectedItemIds.includes(mk.itemId) : undefined}
                        activeTooltipId={activeTooltipId}
                        setActiveTooltipId={setActiveTooltipId}
                    />
                ))}
            </div>
        </div>
    );
};